import type { RemoteWorkerComposeInfo } from "@lib/remote-compose.ts";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface ProvisionInfo {
	hasOrgKey: boolean;
	orgKeyLower: string;
	registryHost: string;
	/** Resolved server-side, so it honours WORKER_IMAGE_REPO. No tag. */
	workerImageRepo: string;
	workerImageTag: string;
	/** Resolved server-side. No tag. */
	agentImageRepo: string;
	/** The agent's own tag — it is not released in step with the worker. */
	agentImageTag: string;
	saasApiUrl: string;
	dockerNetwork: string;
	/** Null when no publicly reachable origin is configured for remote workers. */
	remoteReleaseOrigin: string | null;
	remoteReleaseOriginProblem: string | null;
}

/**
 * What this manager would hand a worker: registry path, image tag, and whether a
 * remote worker has a release origin it can actually reach.
 *
 * One hook rather than a query per caller, because two queries under the same key
 * with different result shapes is a cache that returns whichever ran first.
 */
export function useProvisionInfo() {
	return useQuery({
		queryKey: ["provision-info"],
		queryFn: async (): Promise<ProvisionInfo | null> => {
			const { data } = await api.api.system["provision-info"].get();
			if (!data || data.success !== true) return null;
			return {
				hasOrgKey: data.hasOrgKey,
				orgKeyLower: data.orgKeyLower,
				registryHost: data.registryHost,
				workerImageRepo: data.workerImageRepo,
				workerImageTag: data.workerImageTag,
				agentImageRepo: data.agentImageRepo,
				agentImageTag: data.agentImageTag,
				saasApiUrl: data.saasApiUrl,
				dockerNetwork: data.dockerNetwork,
				remoteReleaseOrigin: data.remoteReleaseOrigin,
				remoteReleaseOriginProblem: data.remoteReleaseOriginProblem,
			};
		},
		staleTime: 30_000,
	});
}

/**
 * The subset the compose generator needs, with the tag the operator picked
 * rather than this manager's default — the review step has to show the file that
 * will actually be written.
 */
export function toComposeInfo(
	info: ProvisionInfo,
	imageTag?: string,
): RemoteWorkerComposeInfo {
	return {
		workerImageRepo: info.workerImageRepo,
		workerImageTag: imageTag?.trim() || info.workerImageTag,
		agentImageRepo: info.agentImageRepo,
		agentImageTag: info.agentImageTag,
		saasApiUrl: info.saasApiUrl,
		releaseOrigin: info.remoteReleaseOrigin,
	};
}
